<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { quotationsApi } from '$lib/api/quotations';
  import { adminUsersApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import QuoteCard from './QuoteCard.svelte';
  import ApprovedList from './ApprovedList.svelte';
  import ArchivedList from './ArchivedList.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import { legalActions, columnKeyForStatus, type ColKey, type LegalAction } from './dragTransitions';
  import type { Quotation, QuotationStatus, Paginated } from '$lib/types';
  import { QUOTE_VERTICALS, QUOTE_VERTICAL_META, type QuoteVertical } from '$lib/types';

  const { search, onOpen }: { search: string; onOpen: (id: number) => void } = $props();

  // Search now hits the server (GET /quotations?q=), which does the
  // client_name/project_name/quotation_number matching itself — debounce so
  // it's not one request per keystroke.
  let debouncedSearch = $state('');
  let debounceTimer: ReturnType<typeof setTimeout>;
  $effect(() => {
    const value = search;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => (debouncedSearch = value), 300);
  });

  // Board vs. Approved: Approved is a terminal, ever-growing state (see
  // dragTransitions.ts's doc comment) — modeling it as a 5th kanban column
  // would make it an unbounded dumping ground nobody drags out of, and it'd
  // crowd out the columns doing real work. It's a searchable/paginated list
  // instead (ApprovedList.svelte), reusing the same server-side q=/page=
  // that already exists on GET /quotations.
  let view = $state<'board' | 'approved' | 'archived'>('board');

  type Col = { key: ColKey; label: string; dot: string; status: QuotationStatus };
  const COLUMNS: Col[] = [
    { key: 'draft', label: 'Draft', dot: 'var(--tym-dark-gray)', status: 'draft' },
    { key: 'submitted', label: 'Submitted', dot: 'var(--warn)', status: 'submitted' },
    { key: 'priced', label: 'In review', dot: 'var(--tym-teal)', status: 'priced' },
    { key: 'revised', label: 'Revised', dot: 'var(--tym-danger)', status: 'revised' }
  ];

  // Each column is its own paginated query, starting at 30 rows and growing
  // to the server's 100-row-per-request cap via "Load more"; `count` (from
  // the server, accurate regardless of how much is loaded) drives that
  // button and the vertical-chip badges below.
  //
  // ponytail: vertical-chip filtering (and its badge counts) still applies
  // client-side over whatever's currently loaded per column, not a
  // server-side vertical scope for the board fetch itself — see the prior
  // version of this file's note; unchanged by the board/approved split.
  let perPageByCol = $state<Record<ColKey, number>>({ draft: 30, submitted: 30, priced: 30, revised: 30 });

  const columnQueries = COLUMNS.map((col) =>
    createQuery(() => ({
      queryKey: ['pipeline-quotations', col.key, debouncedSearch, perPageByCol[col.key]],
      queryFn: () =>
        quotationsApi.list({
          status: col.status,
          q: debouncedSearch || undefined,
          page: 1,
          per_page: perPageByCol[col.key]
        })
    }))
  );

  function loadMore(key: ColKey) {
    perPageByCol = { ...perPageByCol, [key]: Math.min(100, perPageByCol[key] + 30) };
  }

  // Quote cards/rows show the owning designer's name — the quotation API
  // only returns created_by_id, not a name. Non-admins only ever see their
  // own quotes (server-scoped), so they're always "You"; for admins,
  // resolve names via the admin users list (an endpoint they already have
  // access to) rather than inventing a new lookup endpoint.
  const usersQuery = createQuery(() => ({
    queryKey: ['pipeline-designer-names'],
    queryFn: () => adminUsersApi.list({ per_page: 100 }),
    enabled: session.isAdmin
  }));

  function designerLabel(q: Quotation): string {
    if (q.created_by_id === session.me?.user.id) return 'You';
    const u = usersQuery.data?.results.find((r) => r.id === q.created_by_id);
    return u?.name || u?.email || `User #${q.created_by_id}`;
  }

  const vFilter = $derived(route.current.vertical ?? 'all');
  function setFilter(v: string) {
    route.push({ tab: 'pipeline', vertical: v });
  }

  const approvedCountQuery = createQuery(() => ({
    queryKey: ['approved-quotations-count', vFilter],
    queryFn: () =>
      quotationsApi.list({ status: 'approved', vertical: vFilter === 'all' ? undefined : vFilter, per_page: 1 })
  }));

  const allLoaded = $derived(columnQueries.flatMap((cq) => cq.data?.results ?? []));
  const totalCount = $derived(columnQueries.reduce((sum, cq) => sum + (cq.data?.count ?? 0), 0));
  const byVertical = (v: QuoteVertical) => allLoaded.filter((q) => q.vertical === v);
  const isLoading = $derived(columnQueries.some((cq) => cq.isLoading));
  const firstError = $derived(columnQueries.find((cq) => cq.isError)?.error);

  function columnRows(colIdx: number): Quotation[] {
    const rows = columnQueries[colIdx].data?.results ?? [];
    return vFilter === 'all' ? rows : rows.filter((q) => q.vertical === vFilter);
  }

  // --- "Awaiting me": the highest-value quick filter on the board. Admins
  // care about submitted (needs pricing) + priced (needs approval); a
  // designer's equivalent is their own returned/revised work. Just a column
  // filter, not a new page — expand only if this genuinely stops covering
  // the need. ---
  let awaitingMeOnly = $state(false);
  const awaitingKeys = $derived<ColKey[]>(session.isAdmin ? ['submitted', 'priced'] : ['revised']);
  const visibleColumnIdx = $derived(
    COLUMNS.map((_, i) => i).filter((i) => !awaitingMeOnly || awaitingKeys.includes(COLUMNS[i].key))
  );
  const awaitingCount = $derived(
    COLUMNS.reduce((sum, c, i) => (awaitingKeys.includes(c.key) ? sum + (columnQueries[i].data?.count ?? 0) : sum), 0)
  );

  // --- Drag-and-drop ---------------------------------------------------
  //
  // Legal drop targets are computed once at dragstart from the SAME
  // lifecycle predicates (via dragTransitions.legalActions) that gate the
  // keyboard-reachable per-card <select> — one source of truth for "what
  // can this quote legally do next" driving both affordances. Dropping
  // calls the exact lifecycle action endpoint that transition corresponds
  // to (submit/approve/return-for-pricing) — never a generic status
  // setter. Illegal columns never get e.preventDefault() in dragover, so
  // the browser shows its native no-drop cursor and rejects the drop
  // itself; CSS below additionally dims them for the whole drag so it's
  // obvious before the user even hovers.

  type Dragging = { quote: Quotation; fromColKey: ColKey; actions: LegalAction[]; validCols: Set<ColKey> };
  let dragging = $state<Dragging | null>(null);
  let dragOverCol = $state<ColKey | null>(null);

  function handleDragStart(quote: Quotation) {
    const fromColKey = columnKeyForStatus(quote.status);
    if (!fromColKey) return; // shouldn't happen — approved quotes aren't on the board
    const acts = legalActions(quote, session.me?.user.id, session.isAdmin);
    const validCols = new Set(
      acts.filter((a) => a.targetCol && a.targetCol !== fromColKey).map((a) => a.targetCol as ColKey)
    );
    dragging = { quote, fromColKey, actions: acts, validCols };
  }

  function handleDragEnd() {
    dragging = null;
    dragOverCol = null;
  }

  function handleColDragOver(e: DragEvent, colKey: ColKey, colIdx: number) {
    if (!dragging || !dragging.validCols.has(colKey)) return; // no preventDefault => native "not-allowed" cursor, drop rejected
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    dragOverCol = colKey;
    const el = colBodyEls[colIdx];
    if (el) autoScroll(el, e.clientY);
  }

  function handleColDragLeave(colKey: ColKey) {
    if (dragOverCol === colKey) dragOverCol = null;
  }

  function handleDrop(e: DragEvent, colKey: ColKey) {
    e.preventDefault();
    dragOverCol = null;
    if (!dragging) return;
    const { quote, fromColKey, actions } = dragging;
    dragging = null;
    const action = actions.find((a) => a.targetCol === colKey);
    if (!action) return; // unreachable in practice — dragover already refused this column
    runAction(quote, action, fromColKey);
  }

  // --- Confirmation + execution (shared by drag drop and the per-card
  // keyboard <select>) ---

  let pendingConfirm = $state<{ quote: Quotation; action: LegalAction; fromColKey: ColKey } | null>(null);

  function runAction(quote: Quotation, action: LegalAction, fromColKey: ColKey) {
    if (action.id === 'price-navigate') {
      // priceQuotation (POST /quotations/:id/price) takes no payload — it
      // recalculates from whatever's already on the line items, it doesn't
      // need one fabricated. But that also means firing it blind from the
      // board (which shows no line items) would silently lock in zero
      // pricing for a quote nobody has actually priced yet. Navigate into
      // the quote instead, where the admin can see/edit real line-item
      // prices before saving pricing for real.
      onOpen(quote.id);
      return;
    }
    if (action.confirm) {
      pendingConfirm = { quote, action, fromColKey };
      return;
    }
    execute(quote, action, fromColKey);
  }

  function confirmPending() {
    if (!pendingConfirm) return;
    const { quote, action, fromColKey } = pendingConfirm;
    pendingConfirm = null;
    execute(quote, action, fromColKey);
  }

  const queryClient = useQueryClient();

  function statusForAction(id: LegalAction['id']): QuotationStatus {
    switch (id) {
      case 'submit':
        return 'submitted';
      case 'return-for-pricing':
        return 'revised';
      case 'approve':
        return 'approved';
      default:
        return 'draft';
    }
  }

  function setColumnData(colKey: ColKey, updater: (old: Paginated<Quotation>) => Paginated<Quotation>) {
    queryClient.setQueriesData<Paginated<Quotation>>({ queryKey: ['pipeline-quotations', colKey] }, (old) =>
      old ? updater(old) : old
    );
  }

  function removeFromColumn(colKey: ColKey, quoteId: number) {
    setColumnData(colKey, (old) => ({
      ...old,
      results: old.results.filter((q) => q.id !== quoteId),
      count: Math.max(0, old.count - 1)
    }));
  }

  function addToColumn(colKey: ColKey, quote: Quotation) {
    setColumnData(colKey, (old) => ({ ...old, results: [quote, ...old.results], count: old.count + 1 }));
  }

  // Optimistic update with rollback: the card moves immediately (both for
  // drag and the keyboard action), then the real server call runs. Approve
  // has no targetCol (Approved isn't a board column — the card just leaves
  // the board). On any failure the optimistic edit is undone exactly and
  // the server's own error message is surfaced — the UI never keeps
  // showing a state the server rejected.
  async function execute(quote: Quotation, action: LegalAction, fromColKey: ColKey) {
    removeFromColumn(fromColKey, quote.id);
    if (action.targetCol) {
      addToColumn(action.targetCol, { ...quote, status: statusForAction(action.id) });
    }
    try {
      const call =
        action.id === 'submit'
          ? quotationsApi.submit
          : action.id === 'approve'
            ? quotationsApi.approve
            : quotationsApi.returnForPricing;
      await call(quote.id);
      if (action.id === 'approve') {
        toast.success('Quotation approved — it moved off the board into Approved.', {
          label: 'View',
          onClick: () => onOpen(quote.id)
        });
      } else if (action.id === 'submit') {
        toast.success('Submitted for pricing.');
      } else {
        toast.success('Sent back to the designer for pricing edits.');
      }
    } catch (err) {
      if (action.targetCol) removeFromColumn(action.targetCol, quote.id);
      addToColumn(fromColKey, quote);
      toast.error(getErrorMessage(err));
    } finally {
      queryClient.invalidateQueries({ queryKey: ['pipeline-quotations'] });
      if (action.id === 'approve') {
        queryClient.invalidateQueries({ queryKey: ['approved-quotations'] });
        queryClient.invalidateQueries({ queryKey: ['approved-quotations-count'] });
      }
    }
  }

  // --- Scroll affordance: hidden scrollbar chrome (see app.css's
  // .pq-kanban-col-body), but a fade mask signals real overflow, and
  // dragging near a column's top/bottom edge auto-scrolls it (there's no
  // visible scrollbar left to grab). ---

  let colBodyEls = $state<(HTMLDivElement | null)[]>(COLUMNS.map(() => null));
  let colOverflowing = $state<boolean[]>(COLUMNS.map(() => false));
  let colAtBottom = $state<boolean[]>(COLUMNS.map(() => true));

  function updateOverflow(i: number) {
    const el = colBodyEls[i];
    if (!el) return;
    colOverflowing[i] = el.scrollHeight > el.clientHeight + 1;
    colAtBottom[i] = el.scrollHeight - el.scrollTop - el.clientHeight < 2;
  }

  function updateAllOverflow() {
    COLUMNS.forEach((_, i) => updateOverflow(i));
  }

  // Re-check whenever a column's row count changes (load more, optimistic
  // move, filter change) — queued so it runs after the DOM reflects it.
  $effect(() => {
    void columnQueries.map((cq) => cq.data);
    void awaitingMeOnly;
    queueMicrotask(updateAllOverflow);
  });

  // ponytail: fires on every dragover tick (the browser's own throttle,
  // ~a few times/sec while stationary, continuously while moving) rather
  // than a requestAnimationFrame loop — simple and good enough for a
  // kanban column; upgrade to rAF if the scroll ever feels too coarse.
  const EDGE_PX = 40;
  const SCROLL_STEP_PX = 18;
  function autoScroll(el: HTMLElement, clientY: number) {
    const rect = el.getBoundingClientRect();
    if (clientY - rect.top < EDGE_PX) {
      el.scrollTop -= SCROLL_STEP_PX;
    } else if (rect.bottom - clientY < EDGE_PX) {
      el.scrollTop += SCROLL_STEP_PX;
    }
  }
</script>

<svelte:window onresize={updateAllOverflow} />

<div class="flex-1 flex flex-col overflow-hidden">
  <div class="px-4 lg:px-7 pt-4 flex items-center gap-2.5 flex-wrap">
    <button type="button" class="tym-chip {view === 'board' ? 'active' : ''}" onclick={() => (view = 'board')}>
      Board
    </button>
    <button type="button" class="tym-chip {view === 'approved' ? 'active' : ''}" onclick={() => (view = 'approved')}>
      Approved
      <span
        class="text-[11px] font-semibold rounded-full px-[7px] {view === 'approved'
          ? 'bg-white/20'
          : 'bg-toyama-light-carbon text-dark-gray'}"
      >
        {approvedCountQuery.data?.count ?? 0}
      </span>
    </button>
    <button type="button" class="tym-chip {view === 'archived' ? 'active' : ''}" onclick={() => (view = 'archived')}>
      Archived
    </button>
    {#if view === 'board'}
      <span class="w-px h-5 bg-toyama-cloudy-gray mx-0.5"></span>
      <button
        type="button"
        class="tym-chip {awaitingMeOnly ? 'active' : ''}"
        onclick={() => (awaitingMeOnly = !awaitingMeOnly)}
        title={session.isAdmin ? 'Submitted quotes awaiting pricing + priced quotes awaiting approval' : 'Your quotes returned for pricing edits'}
      >
        Awaiting me
        <span
          class="text-[11px] font-semibold rounded-full px-[7px] {awaitingMeOnly
            ? 'bg-white/20'
            : 'bg-toyama-light-carbon text-dark-gray'}"
        >
          {awaitingCount}
        </span>
      </button>
    {/if}
  </div>

  <div class="px-4 lg:px-7 pt-2.5 pb-1 flex gap-2 overflow-x-auto shrink-0">
    <button type="button" class="tym-chip {vFilter === 'all' ? 'active' : ''}" onclick={() => setFilter('all')}>
      All
      {#if view !== 'archived'}
      <span class="text-[11px] font-semibold rounded-full px-[7px] {vFilter === 'all' ? 'bg-white/20' : 'bg-toyama-light-carbon text-dark-gray'}">
        {view === 'approved' ? (approvedCountQuery.data?.count ?? 0) : totalCount}
      </span>
      {/if}
    </button>
    {#each QUOTE_VERTICALS as v (v)}
      <button type="button" class="tym-chip {vFilter === v ? 'active' : ''}" onclick={() => setFilter(v)}>
        {QUOTE_VERTICAL_META[v].label}
        {#if view === 'board'}
        <span class="text-[11px] font-semibold rounded-full px-[7px] {vFilter === v ? 'bg-white/20' : 'bg-toyama-light-carbon text-dark-gray'}">
          {byVertical(v).length}
        </span>
        {/if}
      </button>
    {/each}
  </div>

  {#if view === 'approved'}
    <ApprovedList search={debouncedSearch} vertical={vFilter} {designerLabel} {onOpen} />
  {:else if view === 'archived'}
    <ArchivedList search={debouncedSearch} vertical={vFilter} {designerLabel} {onOpen} />
  {:else if isLoading}
    <p class="tym-loading-state">Loading pipeline&hellip;</p>
  {:else if firstError}
    <p class="tym-error-state m-7">{getErrorMessage(firstError)}</p>
  {:else}
    <div class="flex-1 overflow-x-auto overflow-y-hidden px-4 lg:px-7 pt-3.5 pb-6 max-lg:snap-x max-lg:snap-mandatory max-lg:scroll-px-4">
      <div class="flex gap-4 h-full items-stretch min-w-min">
        {#each visibleColumnIdx as i (COLUMNS[i].key)}
          {@const col = COLUMNS[i]}
          {@const rows = columnRows(i)}
          {@const cq = columnQueries[i]}
          {@const hasMore = (cq.data?.count ?? 0) > (cq.data?.results.length ?? 0)}
          {@const isValidTarget = dragging?.validCols.has(col.key) ?? false}
          <div
            class="pq-kanban-col max-lg:snap-start
              {dragging && isValidTarget ? 'pq-kanban-col--drop-valid' : ''}
              {dragging && !isValidTarget ? 'pq-kanban-col--drop-invalid' : ''}
              {dragOverCol === col.key && isValidTarget ? 'pq-kanban-col--drop-over' : ''}"
            role="group"
            aria-label="{col.label} quotations"
            ondragover={(e) => handleColDragOver(e, col.key, i)}
            ondragenter={(e) => handleColDragOver(e, col.key, i)}
            ondragleave={() => handleColDragLeave(col.key)}
            ondrop={(e) => handleDrop(e, col.key)}
          >
            <div class="flex items-center justify-between px-4 pt-4 pb-3">
              <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full" style="background:{col.dot}"></span>
                <span class="font-heading font-bold text-[13.5px] text-navy tracking-[.01em]">{col.label}</span>
              </div>
              <span class="text-xs font-semibold text-dark-gray bg-[var(--tym-white)] rounded-full px-[9px] py-px border border-toyama-cloudy-gray">
                {vFilter === 'all' ? (cq.data?.count ?? rows.length) : rows.length}
              </span>
            </div>
            <div
              bind:this={colBodyEls[i]}
              class="pq-kanban-col-body flex-1 px-3 pb-3.5 flex flex-col gap-3 {colOverflowing[i] && !colAtBottom[i] ? 'pq-kanban-col-body--fade' : ''}"
              onscroll={() => updateOverflow(i)}
            >
              {#each rows as q (q.id)}
                <QuoteCard
                  quote={q}
                  designerLabel={designerLabel(q)}
                  onOpen={() => onOpen(q.id)}
                  actions={legalActions(q, session.me?.user.id, session.isAdmin)}
                  onDragStart={() => handleDragStart(q)}
                  onDragEnd={handleDragEnd}
                  onAction={(action) => runAction(q, action, col.key)}
                />
              {:else}
                <div class="text-center py-5 px-2 text-dark-gray text-[12.5px]">Nothing here</div>
              {/each}
              {#if hasMore}
                <button
                  type="button"
                  class="btn-secondary w-full justify-center !py-2 text-[12.5px]"
                  onclick={() => loadMore(col.key)}
                >
                  Load more
                </button>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>

{#if pendingConfirm}
  <Modal title="Confirm" onClose={() => (pendingConfirm = null)}>
    <p class="text-[13.5px] text-toyama-heavy-carbon mb-4 leading-relaxed">{pendingConfirm.action.confirm}</p>
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary" onclick={() => (pendingConfirm = null)}>Cancel</button>
      <button type="button" class="btn-primary" onclick={confirmPending}>Continue</button>
    </div>
  </Modal>
{/if}
